import { useState, type FormEvent } from 'react'

import { Button } from '@/components/ui/Button'
import { SITE } from '@/config/site'
import { useI18n } from '@/i18n'
import { isBackendConnected, subscribeToNewsletter } from '@/lib/submissions'

type Status = 'idle' | 'submitting' | 'success' | 'error'

/** Single-field email capture. Inert, with an explanation, until a backend is connected. */
export function NewsletterForm({ source = 'homepage' }: { source?: string }) {
  const { t, locale } = useI18n()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatus('submitting')
    setError(null)

    const result = await subscribeToNewsletter({
      email: email.trim().toLowerCase(),
      locale,
      source,
    })

    if (!result.ok) {
      setStatus('error')
      setError(
        result.reason === 'not-configured'
          ? `${t.form.errorOffline} ${SITE.email}`
          : t.form.errorGeneric,
      )
      return
    }

    setStatus('success')
    setEmail('')
  }

  if (status === 'success') {
    return (
      <p role="status" className="text-sm text-gold">
        {t.form.successTitle} — {t.form.successBody}
      </p>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <label htmlFor="newsletter-email" className="sr-only">
          {t.form.email}
        </label>
        {/* text-base at mobile widths stops iOS Safari zooming on focus. */}
        <input
          id="newsletter-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t.form.email}
          className="min-h-11 w-full flex-1 rounded-full border border-line bg-surface px-5 text-base text-parchment placeholder:text-faint focus:border-gold focus:outline-none sm:text-sm"
        />
        <Button
          type="submit"
          disabled={status === 'submitting' || !isBackendConnected}
          className="sm:w-auto"
        >
          {status === 'submitting' ? t.form.submitting : t.form.subscribe}
        </Button>
      </div>

      {!isBackendConnected ? (
        <p className="mt-2.5 text-xs text-faint">
          {t.form.errorOffline}{' '}
          <a href={`mailto:${SITE.email}`} className="break-all text-gold underline">
            {SITE.email}
          </a>
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2.5 text-xs text-ember">
          {error}
        </p>
      ) : null}
    </form>
  )
}
